import {
  Skeleton,
  SubHeader,
  Tag,
  useBoundingClientRect,
  useWindowResize,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, ReactNode, memo, useMemo, useRef } from 'react';
import styles from './Layout.module.scss';

const cx = classnames.bind(styles);

type Props = {
  children: ReactNode;
  title?: string;
  showHeaderBorderTop?: boolean;
  showHeaderBorderBottom?: boolean;
  headerRight?: ReactNode;
  hideContentPadding?: boolean;
  isLoading?: boolean;
  reportStatus?: string;
  titleLink?: ReactNode;
  titleExtra?: ReactNode;
  backLink?: ReactNode;
  className?: string;
};

export const Layout: FC<Props> = memo(
  ({
    children,
    title,
    showHeaderBorderTop,
    showHeaderBorderBottom,
    headerRight,
    hideContentPadding = false,
    isLoading,
    reportStatus,
    titleLink,
    titleExtra,
    backLink,
    className,
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { innerHeight } = useWindowResize();
    const { top } = useBoundingClientRect(ref);
    const contentContainerStyle = useMemo(
      () => ({
        height: innerHeight - top,
      }),
      [innerHeight, top]
    );
    return (
      <div className={cx(styles['container'])}>
        <div
          className={cx(styles['top'], className, {
            [styles['show-border-top']]: showHeaderBorderTop,
            [styles['show-border-bottom']]: showHeaderBorderBottom,
          })}
        >
          {isLoading ? (
            <div className={cx({ [styles['loading']]: isLoading })}>
              <Skeleton.Input size='default' block />
            </div>
          ) : (
            <div className={cx(styles['header'])}>
              <SubHeader title={title || ''} backLink={backLink} />
              {titleLink}
              {reportStatus && (
                <Tag
                  className={cx(styles['report-status'])}
                  closable={false}
                  outline={false}
                >
                  {reportStatus}
                </Tag>
              )}
              {titleExtra && (
                <div className={cx(styles['title-extra'])}>{titleExtra}</div>
              )}
            </div>
          )}
          {headerRight}
        </div>
        <div
          ref={ref}
          className={cx(styles['bottom'], {
            [styles['content-padding']]: !hideContentPadding,
          })}
          style={contentContainerStyle}
        >
          <main className={cx(styles['content'])}>{children}</main>
        </div>
      </div>
    );
  }
);
Layout.displayName = 'Layout';
